import React, { useState, useEffect } from "react";
import {
  FormControl,
  FormHelperText,
  TextField,
  Typography,
  InputAdornment,
  Popover,
  Box,
  Stack,
  Button,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import { DateRange } from "react-date-range";
import { format } from "date-fns";
import { zhTW, enUS, zhCN, ko, ja } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import emissionViewStore from "../../Store/marco/emissionViewStore";
import commonStore from "../../Store/marco/commonStore";
import {
  calendarPeriod,
  isDateInPeriod,
  pickerBounds,
} from "../utils/reportingPeriod";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

export const DateRangePicker = observer(({
  confirmedRange,
  setConfirmedRange,
  error,
  clearDateRangeError,
  year,
  disabled,
}) => {
  const { t } = useTranslation("ActivityMain");

  const period = emissionViewStore.reportingPeriod ?? calendarPeriod(year);

  // 初始日期範圍為整個報告期間
  const getInitialRange = () => ({
    startDate: period.start.toDate(),
    endDate: period.end.toDate(),
    key: "selection",
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [range, setRange] = useState([getInitialRange()]);

  const handleOpen = (event) => {
    if (confirmedRange) setRange([confirmedRange]);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleConfirm = () => {
    setConfirmedRange(range[0]);
    handleClose();
  };

  const handleClear = () => {
    setRange([getInitialRange()]);
    setConfirmedRange(null);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? "date-range-popover" : undefined;

  const displayText = confirmedRange
    ? `${format(confirmedRange.startDate, "yyyy/MM/dd")} ~ ${format(
        confirmedRange.endDate,
        "yyyy/MM/dd",
      )}`
    : "";

  const bounds = pickerBounds(year);
  const minDate = bounds.minDate.toDate();
  const maxDate = bounds.maxDate.toDate();

  const dateFnsLocale = {
    zh_tw: zhTW,
    en: enUS,
    zh: zhCN,
    korean: ko,
    jp: ja,
  };

  useEffect(() => {
    if (!confirmedRange) {
      setRange([getInitialRange()]);
    }
  }, [year, period.start.valueOf(), period.end.valueOf()]);

  return (
    <FormControl fullWidth sx={{ my: 1, minWidth: 80 }} error={error}>
      <Typography sx={{ marginBottom: 1 }}>
        <span style={{ color: "red" }}>*</span> {t("Date Range")}
      </Typography>
      <TextField
        placeholder={t("Please select date range")}
        value={displayText}
        onClick={handleOpen}
        readOnly
        fullWidth
        InputLabelProps={{ shrink: false }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <EventIcon color="success" sx={{ cursor: "pointer" }} />
            </InputAdornment>
          ),
        }}
      />

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2 }}>
          <DateRange
            editableDateInputs={true}
            onChange={(item) => {
              setRange([item.selection]);
              clearDateRangeError();
              console.log("range", range);
            }}
            moveRangeOnFirstSelection={false}
            ranges={range}
            minDate={minDate}
            maxDate={maxDate}
            disabledDay={(date) => !isDateInPeriod(dayjs(date), period)}
            shownDate={confirmedRange?.startDate ?? period.start.toDate()}
            locale={dateFnsLocale[commonStore.lang]}
            disabled={disabled}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={1}>
            <Button
              variant="outlined"
              onClick={handleClear}
              sx={{ textTransform: "none" }}
              fullWidth
            >
              {t("Clear")}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              sx={{ textTransform: "none" }}
              fullWidth
            >
              {t("Confirm")}
            </Button>
          </Stack>
        </Box>
      </Popover>
      {error && (
        <FormHelperText>{t("Please select date range")}</FormHelperText>
      )}
    </FormControl>
  );
});
